import React, { useState, Key } from "react";
import { createModel } from "hox";
import { TablePaginationConfig } from "antd/es/table/interface";
import { PaginationProps } from "antd/es/pagination";
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE, mianCode } from "@/utils/constants";
import { IPgProps, ISearchPage } from "@/types/ScmTypes";
import { globalPrompt } from "@/components/message";
import { convertRes2Blob } from "@/utils/exportFile";
import { transPgToPagination, formatDiscount, switchNum } from "@/utils/utils";
import { itemService } from "./itemService";
import { IItemSearch, IItemDetail, IItemFields, IItemFieldsAdd } from "./itemType"
import { DictCodeEnmu, sourceAppOrBranch } from "@/types/VrmSaDictEnums"
import { useDictModel } from "../dict/dictModel";
import { useGlobalModel } from "@/model/globalModel";
import zh_CN from "@/locale/zh-CN";
import en_US from "@/locale/en-US";
import { useLockFn } from "ahooks";
export const useItemModel = createModel(function () {
    const { getTreeNodeName } = useDictModel();
    const { user, setLocale } = useGlobalModel();
    let LanguageArr: any = {
        "zh-CN": zh_CN,
        "en-US": en_US
    }
    const Locale = setLocale(LanguageArr)
    const initPgCfg: TablePaginationConfig = { pageSize: DEFAULT_PAGE_SIZE, current: DEFAULT_FIRST_PAGE_NUM }
    //列表数据
    const [dataSource, setDataSource] = useState<IItemDetail[]>([]);
    //查询条件
    const [searchParam, setSearchParam] = useState<IItemFields>({});
    //分页参数
    const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);
    //表格多选id值
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    //编辑表单缓存
    const [editFormVal, setEditFormVal] = useState<IItemFields>({});
    //新增表单缓存
    const [addFormVal, setAddFormVal] = useState<IItemFields>({
        workHours: 1,
        price: 0,
        amount: 0,
        enable: 1
    });

    //设置搜索value
    const updateSearchParam = (updateParam: IItemFields) => {
        setSearchParam({ ...searchParam, ...updateParam })
    }
    //重置搜索参数
    const resetSearchParam = () => {
        setSearchParam({});
        setSelectedRowKeys([]);
        loadData(initPgCfg, {});
    }
    //列表查询
    const loadData = async (page?: ISearchPage, searchValue: IItemFields = searchParam) => {
        const searchParams = {
            ...searchValue,
            pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
            pageNum: (page?.pageNum || page?.current) ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
        }
        let { retData } = await itemService.page(searchParams);
        const { records, ...pg } = retData;
        setPgCfg(transPgToPagination(pg));
        setDataSource(records);
    }
    //表单新增修改
    const updateAddFormVal = (val: IItemFields) => {
        let newVal = val
        if ("workHours" in val) {
            const workHours = val.workHours as number
            newVal = { ...val, amount: +(addFormVal.price as number * workHours).toFixed(2) }
        }
        if ("price" in val) {
            const price = val.price as number
            newVal = { ...val, amount: +(addFormVal.workHours as number * price).toFixed(2) }
        }
        Object.keys(val).length !== 0 ? setAddFormVal({ ...addFormVal, ...newVal }) : setAddFormVal({});
    }
    //表单新增修改
    const updateEditFormVal = (val: IItemFields) => {
        let newVal = val
        if ("workHours" in val) {
            const workHours = val.workHours as number
            newVal = { ...val, amount: +(editFormVal.price as number * workHours).toFixed(2) }
        }
        if ("price" in val) {
            const price = val.price as number
            newVal = { ...val, amount: +(editFormVal.workHours as number * price).toFixed(2) }
        }
        Object.keys(val).length !== 0 ? setEditFormVal({ ...editFormVal, ...newVal }) : setEditFormVal({});
    }
    //获取一条详情数据 Api
    const getDetailOne = async (id: string) => {
        let { retData } = await itemService.one(id);
        retData && setEditFormVal(retData);
    }
    //表单新增 Api
    const insertDetail = useLockFn(async (data: IItemDetail) => {
        let params = {
            ...data,
            // ...sourceAppOrBranch,
            sourceApp: mianCode,
            branch: mianCode,
            price: Number(data.price),
            workHours: Number(data.workHours),
            enable: data.enable ? 1 : 0,
            typeName: getTreeNodeName(data.typeCode as string)
        }
        let { retData } = await itemService.insert(params);
        retData ?
            // globalPrompt("message", { text: "维修项目新增成功", type: "success" })
            // : globalPrompt("message", { text: "维修项目新增失败", type: "error" });
            globalPrompt("message", { text: Locale["Successfully added"], type: "success" })
            : globalPrompt("message", { text: Locale["Unsuccessfully added"], type: "error" });
        return retData;
    })

    //表单编辑 Api
    const editDetail = useLockFn(async (data: IItemDetail) => {
        console.log(data)
        let params = {
            ...data,
            // ...sourceAppOrBranch,
            sourceApp: mianCode,
            branch: mianCode,
            price: Number(data.price),
            workHours: Number(data.workHours),
            enable: data.enable ? 1 : 0,
            typeName: getTreeNodeName(data.typeCode as string)
        }
        let { retData } = await itemService.update(params);
        retData ?
            globalPrompt("message", { text: Locale["Successfully edited"], type: "success" })
            : globalPrompt("message", { text: Locale["Unsuccessfully edited"], type: "error" });
        return retData;
    })

    //导入
    const importData = async (file: { file: File }) => {
        let { retData } = await itemService.import(file);
        retData ?
            globalPrompt("message", { text: Locale["Successfully imported"], type: "success" })
            : globalPrompt("message", { text: Locale["Unsuccessfully imported"], type: "error" });
        return retData;
    }

    //导出
    const exportData = async (searchValue: IItemSearch = searchParam) => {
        const searchParams = {
            ...searchValue,
            pageSize: pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
            pageNum: pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
        }
        let result = await itemService.export(searchParams);
        convertRes2Blob(result);
    }
    //下载模板
    const downloadTpl = async () => {
        let result = await itemService.downloadTpl();
        convertRes2Blob(result);
        // let { retData } = await itemService.downloadTpl();
        // retData ?
        //     globalPrompt("message", { text: "下载模板成功", type: "success" })
        //     : globalPrompt("message", { text: "下载模板失败", type: "error" });
        // return retData;
    }
    //维修项目启用禁用
    const onDisabled = async (id: string, checked: boolean) => {
        let { retData } = await itemService.disable({ id, enable: checked ? 1 : 0 })
        retData ?
            loadData() && globalPrompt("message", { text: Locale["Successfully edited"], type: "success" })
            : globalPrompt("message", { text: Locale["Unsuccessfully edited"], type: "error" });
        return retData;
    }
    return {
        dataSource,
        searchParam,
        pgCfg,
        selectedRowKeys,
        addFormVal,
        editFormVal,
        setAddFormVal,
        setSelectedRowKeys,
        setPgCfg,
        setSearchParam,
        setDataSource,
        updateSearchParam,
        resetSearchParam,
        loadData,
        updateAddFormVal,
        updateEditFormVal,
        getDetailOne,
        insertDetail,
        editDetail,
        importData,
        exportData,
        downloadTpl,
        onDisabled
    }
})